// src/components/dashboard/AdminPricingPanel.jsx
// Admin panel for managing book printing prices

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Settings as SettingsIcon,
  Percent as PercentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import '../../styles/AdminPricing.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const AdminPricingPanel = () => {
  
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current pricing
  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/quotes/pricing`);
      if (!res.ok) throw new Error('Failed to fetch pricing');
      const data = await res.json();
      setPricing(data);
      setHasChanges(false);
      setError(null);
    } catch (err) {
      setError('Failed to load pricing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionPriceChange = (categoryId, optionId, newPrice) => {
    setPricing(prev => ({
      ...prev,
      categories: prev.categories.map(cat => {
        if (cat.id !== categoryId) return cat;
        return {
          ...cat,
          options: cat.options.map(opt => {
            if (opt.id !== optionId) return opt;
            return { ...opt, price: parseFloat(newPrice) || 0 };
          }),
        };
      }),
    }));
    setHasChanges(true);
  };

  const handleBasePriceChange = (field, value) => {
    setPricing(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
    setHasChanges(true);
  };

  const handlePricePerPageChange = (value) => {
    setPricing(prev => ({
      ...prev,
      categories: prev.categories.map(cat => {
        if (cat.id !== 'pages') return cat;
        return { ...cat, price_per_page: parseFloat(value) || 0 };
      }),
    }));
    setHasChanges(true);
  };

  const handleVolumeDiscountChange = (index, field, value) => {
    setPricing(prev => ({
      ...prev,
      volume_discounts: prev.volume_discounts.map((d, i) => {
        if (i !== index) return d;
        return { ...d, [field]: field === 'min_qty' ? parseInt(value) || 0 : parseFloat(value) || 0 };
      }),
    }));
    setHasChanges(true);
  };

  const addVolumeDiscount = () => {
    setPricing(prev => ({
      ...prev,
      volume_discounts: [
        ...prev.volume_discounts,
        { min_qty: 0, discount_percent: 0 },
      ],
    }));
    setHasChanges(true);
  };

  const removeVolumeDiscount = (index) => {
    setPricing(prev => ({
      ...prev,
      volume_discounts: prev.volume_discounts.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  };

  const savePricing = async () => {
    try {
      setSaving(true);
      const accessToken = localStorage.getItem('rv_access_token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/api/quotes/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(pricing),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save pricing');
      }
      
      setSuccess(true);
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading pricing data...</Typography>
      </Paper>
    );
  }

  if (!pricing) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchPricing}>
            Retry
          </Button>
        }>
          Failed to load pricing data
        </Alert>
      </Paper>
    );
  }

  const pagesCategory = pricing.categories?.find(c => c.id === 'pages');

  return (
    <Paper className="admin-pricing-panel" elevation={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Pricing Management
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchPricing} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Base Prices */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            <SettingsIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
            Base Prices & Fees
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Base Price Per Book"
                type="number"
                size="small"
                value={pricing.base_price || 0}
                onChange={(e) => handleBasePriceChange('base_price', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Setup Fee (One-time)"
                type="number"
                size="small"
                value={pricing.setup_fee || 0}
                onChange={(e) => handleBasePriceChange('setup_fee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Price Per Page"
                type="number"
                size="small"
                value={pagesCategory?.price_per_page || 0}
                onChange={(e) => handlePricePerPageChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Volume Discounts */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            <PercentIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
            Volume Discounts
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {pricing.volume_discounts?.map((discount, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Min Quantity"
                type="number"
                size="small"
                value={discount.min_qty}
                onChange={(e) => handleVolumeDiscountChange(index, 'min_qty', e.target.value)}
                sx={{ width: 150 }}
              />
              <TextField
                label="Discount %"
                type="number"
                size="small"
                value={discount.discount_percent}
                onChange={(e) => handleVolumeDiscountChange(index, 'discount_percent', e.target.value)}
                sx={{ width: 120 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <IconButton 
                color="error" 
                onClick={() => removeVolumeDiscount(index)}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addVolumeDiscount}
            size="small"
          >
            Add Discount Tier
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Category Options */}
      {pricing.categories?.filter(cat => cat.type === 'select').map((category) => (
        <Accordion key={category.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              <span style={{ marginRight: 8 }}>{category.icon}</span>
              {category.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {category.options?.map((option) => (
                <Grid item xs={12} sm={6} key={option.id}>
                  <Box className="admin-pricing-option">
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option.name}
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={option.price}
                      onChange={(e) => handleOptionPriceChange(category.id, option.id, e.target.value)}
                      sx={{ width: 100 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ step: 0.5 }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Save Button */}
      <Box className="admin-pricing-save-btn">
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={savePricing}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
        {hasChanges && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            You have unsaved changes
          </Typography>
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Pricing saved successfully!"
      />
    </Paper>
  );
};

export default AdminPricingPanel;
